import type { ConservatoryStyle } from "../../../mocks/conservatories";

interface StyleDetailPanelProps {
  style: ConservatoryStyle;
  onQuote: (styleName: string) => void;
  onClose: () => void;
}

function RatingBar({ value, max = 5, label, color }: { value: number; max?: number; label: string; color: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-zinc-500 w-24 flex-shrink-0" style={{ fontFamily: "'Raleway', sans-serif" }}>{label}</span>
      <div className="flex-1 h-1.5 bg-stone-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${(value / max) * 100}%`, transition: "width 0.5s ease" }} />
      </div>
      <span className="text-xs font-semibold text-zinc-600 w-4" style={{ fontFamily: "'Raleway', sans-serif" }}>{value}/{max}</span>
    </div>
  );
}

export default function StyleDetailPanel({ style, onQuote, onClose }: StyleDetailPanelProps) {
  return (
    <div className="bg-white rounded-xl border border-amber-200 overflow-hidden animate-[fadeIn_0.3s_ease]">
      {/* Header Image */}
      <div className="relative w-full h-64 overflow-hidden">
        <img
          src={style.image}
          alt={`${style.name} conservatory`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-sm" />
        </button>
        {style.badge && (
          <span className="absolute top-4 left-4 bg-amber-600 text-white text-xs font-bold px-3 py-1 rounded-full" style={{ fontFamily: "'Raleway', sans-serif" }}>
            {style.badge}
          </span>
        )}
        <div className="absolute bottom-4 left-5 right-5">
          <h3 className="text-2xl font-bold text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{style.name}</h3>
          <p className="text-amber-300 text-sm font-semibold" style={{ fontFamily: "'Raleway', sans-serif" }}>{style.tagline}</p>
        </div>
      </div>

      <div className="p-6 space-y-5">
        {/* Description */}
        <p className="text-zinc-600 text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>
          {style.longDesc}
        </p>

        {/* Ratings */}
        <div className="space-y-2.5 bg-stone-50 rounded-lg p-4">
          <RatingBar value={style.spaceRating} label="Floor Space" color="bg-amber-500" />
          <RatingBar value={style.lightRating} label="Natural Light" color="bg-amber-500" />
          <RatingBar value={style.budgetRating} label="Budget Score" color="bg-emerald-500" />
        </div>

        {/* Price */}
        <div className="flex items-center justify-between bg-zinc-900 text-white rounded-lg px-5 py-4">
          <div>
            <p className="text-xs text-stone-400" style={{ fontFamily: "'Raleway', sans-serif" }}>Typical Price Range</p>
            <p className="text-lg font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{style.priceRange}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-stone-400" style={{ fontFamily: "'Raleway', sans-serif" }}>Roof Type</p>
            <p className="text-xs text-amber-400 font-semibold" style={{ fontFamily: "'Raleway', sans-serif" }}>{style.roofType}</p>
          </div>
        </div>

        {/* Features */}
        <div>
          <h4 className="text-sm font-bold text-zinc-800 mb-3" style={{ fontFamily: "'Raleway', sans-serif" }}>Key Features</h4>
          <div className="grid grid-cols-2 gap-1.5">
            {style.features.map((f) => (
              <div key={f} className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-amber-600 text-xs" />
                </div>
                <span className="text-zinc-600 text-xs" style={{ fontFamily: "'Raleway', sans-serif" }}>{f}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Best For */}
        <div>
          <h4 className="text-sm font-bold text-zinc-800 mb-2" style={{ fontFamily: "'Raleway', sans-serif" }}>Best For</h4>
          <div className="flex flex-wrap gap-2">
            {style.bestFor.map((b) => (
              <span key={b} className="bg-amber-50 border border-amber-200 text-amber-700 text-xs px-3 py-1 rounded-full" style={{ fontFamily: "'Raleway', sans-serif" }}>
                {b}
              </span>
            ))}
          </div>
        </div>

        {/* CTA */}
        <button
          onClick={() => onQuote(style.name)}
          className="w-full bg-amber-600 hover:bg-amber-500 text-white font-semibold py-3.5 rounded-md transition-colors whitespace-nowrap cursor-pointer"
          style={{ fontFamily: "'Raleway', sans-serif" }}
        >
          Get a Quote for {style.name}
        </button>
      </div>
    </div>
  );
}
